import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAppStore } from "@/stores/appStore";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import {
  IconDashboard,
  IconCoins,
  IconArrowsExchange,
} from "@tabler/icons-react";
import { navigationConfig } from "@/utils/router";
import { WalletButton } from "@/components/wallet/WalletButton";

const iconMap = {
  shield: IconDashboard, // Using dashboard icon as fallback since we replaced shields with WATR logos
  coins: IconCoins,
  exchange: IconArrowsExchange,
};

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen } = useAppStore();

  return (
    <SidebarProvider defaultOpen={sidebarOpen} onOpenChange={setSidebarOpen}>
      <Sidebar className="border-r border-border bg-card">
        <SidebarHeader className="border-b border-border/50 bg-card/50">
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="flex h-10 w-10 items-center justify-center radius-default">
              <img
                src="/wtr-light.svg"
                alt="WATR Logo"
                className="h-12 w-12 dark:hidden"
              />
              <img
                src="/wtr-dark.svg"
                alt="WATR Logo"
                className="h-12 w-12 hidden dark:block"
              />
            </div>
            <div className="flex flex-col">
              <h2 className="text-md font-semibold tracking-tight">
                E-ERC Playground
              </h2>
              <span className="text-xs text-muted-foreground">v1.0</span>
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent className="px-3 py-4 flex flex-col h-full">
          <div className="space-y-1">
            <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Navigation
            </p>
            <SidebarMenu className="space-y-1">
              {navigationConfig.map((route) => {
                const Icon =
                  iconMap[route.icon as keyof typeof iconMap] || IconDashboard;
                // Check if current path matches the route and extract mode from URL
                const currentParams = new URLSearchParams(location.search);
                const currentMode = currentParams.get("mode");
                const routeParams = new URLSearchParams(
                  route.path.split("?")[1] || "",
                );
                const routeMode = routeParams.get("mode");
                const isActive =
                  location.pathname === route.path.split("?")[0] &&
                  currentMode === routeMode;

                return (
                  <SidebarMenuItem key={route.key}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => navigate(route.path)}
                      className={`
                        flex items-start gap-3 px-1 py-3 radius-default transition-all duration-200 min-h-[46px]
                        hover:bg-accent/50 hover:shadow-sm
                        ${
                          isActive
                            ? "bg-primary/20 text-primary border border-primary/30 shadow-md"
                            : "text-muted-foreground hover:text-foreground"
                        }
                      `}
                    >
                      <Icon
                        size={18}
                        className={`mt-0.5 flex-shrink-0 ${
                          isActive ? "text-primary" : "text-muted-foreground"
                        }`}
                      />
                      <div className="flex flex-col items-start flex-1 min-w-0 overflow-hidden">
                        <span className="text-sm font-medium leading-tight w-full">
                          {route.label}
                        </span>
                        {"description" in route && route.description ? (
                          <span className="text-xs text-muted-foreground leading-tight w-full mt-1">
                            {route.description}
                          </span>
                        ) : null}
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </div>
          {/* Copyright footer */}
          <div className="mt-auto pt-4 px-3">
            <div className="text-xs text-muted-foreground text-center py-2 border-t border-border/50">
              © WATR {new Date().getFullYear()}
            </div>
          </div>
        </SidebarContent>
      </Sidebar>
      <SidebarInset
        className={`${
          sidebarOpen ? "ml-64" : "ml-16"
        } transition-all duration-300`}
      >
        <header className="flex h-20 shrink-0 items-center gap-4 border-b border-border/50 bg-card/30 px-6">
          <div className="flex items-center gap-4">
            {/* <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 dark:from-primary dark:to-primary/80 radius-lg flex items-center justify-center shadow-lg">
              <img
                src="/wtr-light.svg"
                alt="WATR Logo"
                className="w-8 h-8 brightness-0 invert"
              />
            </div> */}
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900 dark:text-slate-100">
                Encrypted ERC-20
              </span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <WalletButton />
          </div>
        </header>
        <div className="flex flex-1 flex-col bg-background/50 min-h-screen">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
